import { useEffect, useRef, useMemo, useCallback, type MutableRefObject } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import type { OpenTab } from '@/components/features/document-tabs'
import { DEFAULT_PRODUCT_BOARD_DOCUMENT_FILE_PATH } from '@/lib/product-board-default-doc'
import { stripOptionalBasecampWorkspaceSegment } from '@/lib/basecamp-url'
import {
  buildAppUrl,
  folderUrlForResolvedDocUuidBar,
  basecampPathnamesEquivalent,
  resolveLegacyPath,
  parseAppPath,
  pathToUrl,
  shouldDeferTabUrlSyncToAddressBar,
  tabYieldsToDocumentDeepLink,
  documentUrlPathsEquivalent,
  findOpenTabForDocumentPath,
  workspaceIdForSlug,
  workspaceIdForDocumentUrlPath,
  normalizeFolderStoragePath,
  isUuid,
  type WorkspaceRouteInfo,
} from '@/lib/app-routes'

import { useWorkspace } from '@/providers/workspace-provider'
import { resolveBasecampTabFromShorthandPath } from '@/lib/basecamp-tab-routing'
import { chatConversationTabId } from '@/hooks/use-chat-surface-registry'
import {
  captureColdLoadDocumentDeepLink,
  isColdLoadDocumentTabResolved,
  shareLinkTabCountsAsOpen,
} from '@/lib/document-deep-link-cold-load'
import {
  getPendingDocumentDeepLink,
  setPendingDocumentDeepLink,
  isDocumentDeepLinkRestorePending,
  shouldRetryPendingDocumentRestore,
} from '@/lib/document-deep-link-pending'
import { canonicalizeIndexUrl, mergeIndexQueryForPath, pathnameOfUrl } from '@/lib/index-query-url'
import { navDebug } from '@/lib/nav-debug'

function logUrlSync(
  direction: 'tab-to-url' | 'url-to-tab',
  outcome: 'navigate' | 'onUrlChange' | 'skip',
  reason: string,
  extra?: { pathname?: string; activeTabId?: string | null; targetUrl?: string },
) {
  navDebug.recordUrlSync({
    direction,
    outcome,
    reason,
    pathname: extra?.pathname,
    activeTabId: extra?.activeTabId,
    targetUrl: extra?.targetUrl,
  })
}

function syncPendingDocumentDeepLinkRef(
  ref: MutableRefObject<{ pathname: string; pathOrId: string } | null>,
  restoreStartedRef?: MutableRefObject<boolean>,
) {
  const pending = getPendingDocumentDeepLink()
  if (pending) {
    if (!ref.current) ref.current = pending
    return
  }
  // Cleared externally (e.g. url-handlers fail-closed) - drop local refs too.
  if (ref.current) {
    ref.current = null
    if (restoreStartedRef) restoreStartedRef.current = false
  }
}

function clearPendingDocumentDeepLink(
  ref: MutableRefObject<{ pathname: string; pathOrId: string } | null>,
  restoreStartedRef: MutableRefObject<boolean>,
) {
  ref.current = null
  setPendingDocumentDeepLink(null)
  restoreStartedRef.current = false
}


function isDocumentDeepLinkPath(
  pathname: string,
  workspaces: WorkspaceRouteInfo[],
): boolean {
  return parseAppPath(pathname, new URLSearchParams(), workspaces).type === 'document'
}

interface UseUrlSyncProps {
  activeTabId: string | null
  openTabs: OpenTab[]
  onUrlChange: (contentId: string, params: UrlChangeParams) => void
}

export interface UrlChangeParams {
  type:
    | 'document'
    | 'explorer'
    | 'integration-basecamp'
    | 'integration-basecamp-todo'
    | 'integration-basecamp-todo-list'
    | 'integration-basecamp-triage'
    | 'integration-voice-memos'
    | 'integration-product-board'
    | 'integration-google-drive'
    | 'integration-google-drive-preview'
    | 'integration-nextcloud'
    | 'integration'
    | 'search'
    | 'person'
    | 'people-index'
    | 'settings'
    | 'workspace-home'
    | 'home'
    | 'edit-history'
  projectId?: string
  todoId?: string
  todoTitle?: string
  integrationId?: string
  driveFileId?: string
  driveMimeType?: string
  driveFileName?: string
  folderPath?: string | null
  section?: 'bookmarks' | 'mystuff' | 'todos' | 'projects'
  /** Organization / workspace UUID from URL (/basecamp/{workspaceId}/... or /workspace/{id}). */
  workspaceId?: string
  /** Workspace slug from /{slug}/basecamp/... (preserved when org list is not loaded yet). */
  workspaceSlug?: string
  listId?: string
  /** Search host named view from ?view= (e.g. activity). */
  searchNamedView?: string
  /** Search keyword from ?q= */
  searchQuery?: string
  /** Search path scope from ?path= */
  searchPath?: string
  /** Activity / Search member filter from ?user= */
  searchUpdatedBy?: string
  /** Search meaning-search from ?semantic=1 */
  searchSemantic?: boolean
  /** Person dossier from /people/:userId */
  personUserId?: string
}

const BASECAMP_WORKSPACE_SLUG_SESSION_KEY = 'flownotes_basecamp_workspace_slug'

function rememberBasecampWorkspaceSlug(slug: string | undefined) {
  if (!slug) return
  try {
    sessionStorage.setItem(BASECAMP_WORKSPACE_SLUG_SESSION_KEY, slug)
  } catch {
    /* ignore */
  }
}

function basecampWorkspaceParams(
  urlIntent: ReturnType<typeof parseAppPath>,
  wsForBasecampUrl?: string
): { workspaceId?: string; workspaceSlug?: string } {
  const slug =
    urlIntent.type === 'extension-basecamp' ? urlIntent.workspaceSlug : undefined
  if (slug) rememberBasecampWorkspaceSlug(slug)
  const workspaceId =
    (urlIntent.type === 'extension-basecamp' && urlIntent.workspaceId) ||
    wsForBasecampUrl ||
    undefined
  return {
    ...(slug ? { workspaceSlug: slug } : {}),
    ...(workspaceId ? { workspaceId } : {}),
  }
}

// Track navigation source to prevent race conditions
type NavigationSource = 'tab-click' | 'browser' | 'programmatic' | null
const navigationSourceRef = { current: null as NavigationSource }
let navigationSourceTimeout: number | null = null

// Navigation serial counter: incremented on every user-initiated back/forward/close.
// handleDocumentClick captures this before any async work and bails out if it has
// changed by the time the fetch resolves, preventing a stale tab replacement from
// overwriting a back-navigation that restored the search view.
let _navSerial = 0
export const bumpNavSerial = (): number => ++_navSerial
export const getNavSerial = (): number => _navSerial

/**
 * Mark that the next navigation is from a user tab click
 * This prevents the URL sync from racing with tab state updates
 */
export const markTabClickNavigation = () => {
  // Clear any existing timeout
  if (navigationSourceTimeout) {
    clearTimeout(navigationSourceTimeout)
  }

  navigationSourceRef.current = 'tab-click'

  // Clear after 2 seconds to handle slow async updates (back/forward navigation, etc.)
  navigationSourceTimeout = window.setTimeout(() => {
    navigationSourceRef.current = null
    navigationSourceTimeout = null
  }, 2000)
}

/**
 * Mark that navigation is programmatic (e.g., folder click)
 * This prevents URL sync from overriding programmatic navigation
 */
export const markProgrammaticNavigation = () => {
  // Clear any existing timeout
  if (navigationSourceTimeout) {
    clearTimeout(navigationSourceTimeout)
    navigationSourceTimeout = null
  }

  navigationSourceRef.current = 'programmatic'

  // Clear after 3 seconds (increased to handle async URL updates and race conditions)
  navigationSourceTimeout = window.setTimeout(() => {
    navigationSourceRef.current = null
    navigationSourceTimeout = null
  }, 3000)
}

/**
 * Clear the tab-click navigation flag
 * Called after URL successfully updates
 */
export const clearTabClickNavigation = () => {
  if (navigationSourceTimeout) {
    clearTimeout(navigationSourceTimeout)
    navigationSourceTimeout = null
  }
  navigationSourceRef.current = null
}

/**
 * Track a failed document load to prevent redirect loops
 * Called when a document fails to load after URL sync tries to open it
 */
let failedLoadsTracker: { failed: Set<string>, lastFailedTime: Map<string, number> } | null = null

export const setFailedLoadsTracker = (tracker: { failed: Set<string>, lastFailedTime: Map<string, number> }) => {
  failedLoadsTracker = tracker
}

export const markFailedDocumentLoad = (documentId: string) => {
  if (failedLoadsTracker) {
    failedLoadsTracker.failed.add(documentId)
    failedLoadsTracker.lastFailedTime.set(documentId, Date.now())
  }
}

/**
 * Clear failed document load tracking
 * Called when a document successfully loads
 */
export const clearFailedDocumentLoad = (documentId: string) => {
  if (failedLoadsTracker) {
    failedLoadsTracker.failed.delete(documentId)
    failedLoadsTracker.lastFailedTime.delete(documentId)
  }
}

/**
 * Synchronizes URL with active tab state
 * - When tab changes → Update URL
 * - When URL changes (back/forward/bookmark) → Update active tab
 */
export function useUrlSync({
  activeTabId,
  openTabs,
  onUrlChange
}: UseUrlSyncProps) {
  const routerNavigate = useNavigate()
  const syncNavigate = useCallback(
    (to: string, options?: { replace?: boolean }) => {
      if (to === '/' && isDocumentDeepLinkRestorePending()) {
        return
      }
      routerNavigate(to, options)
    },
    [routerNavigate],
  )
  const params = useParams<{
    docId?: string
    projectId?: string
    todoId?: string
    listId?: string
    wsId?: string
  }>()
  const location = useLocation()
  // Capture before workspace list loads so Home never owns the first frame.
  captureColdLoadDocumentDeepLink(location.pathname)
  const { workspaces: workspaceList, hideOtherWorkspaces, activeWorkspaceIds } =
    useWorkspace()
  const workspacesRoute: WorkspaceRouteInfo[] = useMemo(
    () =>
      workspaceList.map((w) => ({
        id: w.id,
        slug: w.slug,
        name: w.name,
        contentRoot: w.contentRoot,
      })),
    [workspaceList]
  )

  // Extract IDs from pathname directly to support splat routes with slashes
  const docPathMatch = location.pathname.match(/^\/doc\/(.+)$/)
  const docIdFromPath = docPathMatch ? decodeURIComponent(docPathMatch[1]) : undefined

  // Track if we're currently updating to prevent loops
  const isUpdatingRef = useRef(false)
  const lastUrlRef = useRef<string>('')
  const lastTabIdRef = useRef<string | null>(null)
  const hasInitializedRef = useRef(false)
  // Track failed document loads to prevent loops
  const failedDocumentLoadsRef = useRef<Set<string>>(new Set())
  const lastFailedLoadTimeRef = useRef<Map<string, number>>(new Map())
  // Rate limiting: track last navigation attempt time to prevent rapid-fire loops
  const lastNavigationAttemptRef = useRef<Map<string, number>>(new Map())
  const lastLegacyRedirectRef = useRef<string | null>(null)
  /** Document deep link waiting for workspace list (cold load); survives transient / in the bar. */
  const pendingDocumentDeepLinkRef = useRef<{
    pathname: string
    pathOrId: string
  } | null>(null)
  const pendingRestoreStartedRef = useRef(false)
  /** Last time a cold document deep link dispatched resolve. Throttles retries. */
  const pendingDocRetryAtRef = useRef(0)

  // Export tracker for use by other components
  useEffect(() => {
    setFailedLoadsTracker({
      failed: failedDocumentLoadsRef.current,
      lastFailedTime: lastFailedLoadTimeRef.current
    })
  }, [])

  // Tab changed → Update URL (PASSIVE - doesn't interfere with tab switching)
  useEffect(() => {
    syncPendingDocumentDeepLinkRef(pendingDocumentDeepLinkRef, pendingRestoreStartedRef)
    const hasPendingDoc = isDocumentDeepLinkRestorePending()
    const pathNow = location.pathname

    if (hasPendingDoc && (location.pathname === '/' || location.pathname === '')) {
      logUrlSync('tab-to-url', 'skip', 'pending-doc-root', {
        pathname: pathNow,
        activeTabId,
      })
      return
    }

    // A cold document URL must stay in the bar until that document (or its folder)
    // actually opens. Restored person tabs were replacing it with /people/:id.
    const pendingForBar = getPendingDocumentDeepLink()
    if (
      pendingForBar &&
      navigationSourceRef.current !== 'tab-click' &&
      (location.pathname === pendingForBar.pathname ||
        documentUrlPathsEquivalent(
          location.pathname,
          pendingForBar.pathname,
          workspacesRoute,
        ))
    ) {
      const pendingTab = openTabs.find((tab) => tab.id === activeTabId)
      const resolvedDocTab =
        isColdLoadDocumentTabResolved(pendingTab) &&
        pendingTab!.type !== 'person-view' &&
        pendingTab!.type !== 'explorer'
      if (!resolvedDocTab) {
        logUrlSync('tab-to-url', 'skip', 'pending-deep-link', {
          pathname: pathNow,
          activeTabId,
        })
        return
      }
    }

    const searchParams = new URLSearchParams(location.search)
    const activeTabForBar = openTabs.find((tab) => tab.id === activeTabId)

    // A new browser tab (or in-app link) lands on /doc/{uuid} before filePath
    // exists. The fetch then patches filePath without changing the tab id.
    // Defer + the isUpdatingRef window used to swallow that one rewrite, so
    // the share URL stuck until a reload (filePath already in localStorage).
    const resolvedFolderUrl = folderUrlForResolvedDocUuidBar(
      pathNow,
      activeTabForBar,
      workspacesRoute,
    )
    if (resolvedFolderUrl) {
      if (hasPendingDoc) {
        clearPendingDocumentDeepLink(pendingDocumentDeepLinkRef, pendingRestoreStartedRef)
      }
      lastTabIdRef.current = activeTabId
      lastUrlRef.current = resolvedFolderUrl
      isUpdatingRef.current = true
      logUrlSync('tab-to-url', 'navigate', 'doc-uuid-to-folder', {
        pathname: pathNow,
        activeTabId,
        targetUrl: resolvedFolderUrl,
      })
      syncNavigate(resolvedFolderUrl, { replace: true })
      setTimeout(() => { isUpdatingRef.current = false }, 100)
      return
    }

    const deferBar = shouldDeferTabUrlSyncToAddressBar(
      location.pathname,
      searchParams,
      workspacesRoute,
      activeTabForBar,
    )

    const isDocDeepLink = isDocumentDeepLinkPath(location.pathname, workspacesRoute)
    const tabDrivenUrlSync =
      navigationSourceRef.current === 'programmatic' ||
      navigationSourceRef.current === 'tab-click'
    const activeYieldsDocLink = tabYieldsToDocumentDeepLink(activeTabId, activeTabForBar)

    // Shared/bookmarked document URLs must win over localStorage-restored tabs until cold
    // restore finishes. After that, the active tab wins. Otherwise a stale document URL
    // (weekly) and another open tab (Todos) re-open each other whenever openTabs changes
    // (tab drag). Tab clicks / workspace-scope recovery already skip this defer.
    // Home, inbox thread, and chat are the exception: a warm document click leaves
    // that reader active while the bar already has the document URL. Letting
    // tab-to-URL win writes the reader URL back and the document flashes away.
    if (
      deferBar &&
      !tabDrivenUrlSync &&
      (!isDocDeepLink || hasPendingDoc || activeYieldsDocLink)
    ) {
      logUrlSync('tab-to-url', 'skip', hasPendingDoc ? 'defer-pending' : 'defer-bar', {
        pathname: pathNow,
        activeTabId,
      })
      return
    }

    // Root URL + Home tab: do not push a restored inbox-thread (or other) tab onto the bar.
    const tabForPathGuard = openTabs.find((tab) => tab.id === activeTabId)
    if (
      (location.pathname === '/' || location.pathname === '') &&
      (activeTabId === 'home' || tabForPathGuard?.type === 'home')
    ) {
      if (hasPendingDoc) {
        logUrlSync('tab-to-url', 'skip', 'home-pending-doc', {
          pathname: pathNow,
          activeTabId,
        })
        return
      }
      if (isDocumentDeepLinkPath(lastUrlRef.current, workspacesRoute)) {
        logUrlSync('tab-to-url', 'skip', 'home-keep-deep-link', {
          pathname: pathNow,
          activeTabId,
          targetUrl: lastUrlRef.current,
        })
        return
      }
      lastUrlRef.current = '/'
      return
    }

    // Skip if already syncing
    if (isUpdatingRef.current) {
      return
    }

    // If this tab recently failed to load, don't update URL
    // This prevents loops when a document fails to load and we switch away
    if (activeTabId && failedDocumentLoadsRef.current.has(activeTabId)) {
      const lastFailedTime = lastFailedLoadTimeRef.current.get(activeTabId)
      const timeSinceLastFailure = lastFailedTime ? Date.now() - lastFailedTime : Infinity
      if (timeSinceLastFailure < 10000) {
        logUrlSync('tab-to-url', 'skip', 'failed-load-cooldown', {
          pathname: pathNow,
          activeTabId,
        })
        return
      }
    }

    // RACE CONDITION FIX: Check if tab exists in openTabs before proceeding
    // React batching can cause activeTabId to update before openTabs
    const activeTab = openTabs.find(tab => tab.id === activeTabId)

    const canonicalForTab = activeTab
      ? buildAppUrl({ tab: activeTab, workspaces: workspacesRoute })
      : '/'
    const locationFull = `${location.pathname}${location.search || ''}`
    const cleanedLocation = activeTab
      ? canonicalizeIndexUrl(location.pathname, location.search || '', activeTab)
      : { url: location.pathname, stripped: Boolean(location.search) }

    const pathMatchesActiveTab =
      Boolean(activeTab) &&
      (location.pathname === pathnameOfUrl(canonicalForTab) ||
        basecampPathnamesEquivalent(location.pathname, canonicalForTab) ||
        documentUrlPathsEquivalent(
          location.pathname,
          pathnameOfUrl(canonicalForTab),
          workspacesRoute,
        ))

    // Strip foreign index query (Todos filters leaking onto a document URL, Search q on People).
    // Only when the pathname already belongs to the active tab. If the path is still the
    // previous tab (Todos) while active is Weekly, fall through to buildAppUrl instead of
    // "canonicalizing" the stale Todos path in place.
    if (
      activeTab &&
      pathMatchesActiveTab &&
      cleanedLocation.url !== locationFull
    ) {
      lastTabIdRef.current = activeTabId
      lastUrlRef.current = cleanedLocation.url
      isUpdatingRef.current = true
      logUrlSync('tab-to-url', 'navigate', 'canonicalize-index', {
        pathname: pathNow,
        activeTabId,
        targetUrl: cleanedLocation.url,
      })
      syncNavigate(cleanedLocation.url, { replace: true })
      setTimeout(() => { isUpdatingRef.current = false }, 100)
      return
    }

    // Same tab id: still do not skip when pathname is wrong (tab switch mid-flight) or
    // when foreign query remains (lastTabId was set before canonicalize could strip).
    if (lastTabIdRef.current === activeTabId && pathMatchesActiveTab) {
      return
    }

    // Same index path: keep slug + union allowlisted query (typed filter vs tab/storage emit).
    if (
      activeTab &&
      (location.pathname === pathnameOfUrl(canonicalForTab) ||
        basecampPathnamesEquivalent(location.pathname, canonicalForTab))
    ) {
      const desired = mergeIndexQueryForPath(
        location.pathname,
        location.search || '',
        canonicalForTab,
        activeTab,
      )
      lastTabIdRef.current = activeTabId
      if (desired !== locationFull) {
        lastUrlRef.current = desired
        isUpdatingRef.current = true
        logUrlSync('tab-to-url', 'navigate', 'merge-index-query', {
          pathname: pathNow,
          activeTabId,
          targetUrl: desired,
        })
        syncNavigate(desired, { replace: true })
        setTimeout(() => { isUpdatingRef.current = false }, 100)
        return
      }
      lastUrlRef.current = locationFull
      return
    }
    if (activeTabId && activeTab?.type !== 'explorer') {
      if (!activeTab) {
        return
      }
    }

    if (!activeTab) {
      return
    }

    // Wait for workspace list before rewriting /{slug}/basecamp/... to /basecamp/... (cold login).
    if (
      workspacesRoute.length === 0 &&
      (activeTab.type === 'integration-basecamp-todo' ||
        activeTab.type === 'integration-basecamp-todo-list' ||
        activeTab.type === 'integration-basecamp') &&
      location.pathname.includes('/basecamp/')
    ) {
      logUrlSync('tab-to-url', 'skip', 'wait-workspaces', {
        pathname: pathNow,
        activeTabId,
      })
      return
    }

    const targetUrl = buildAppUrl({ tab: activeTab, workspaces: workspacesRoute })

    // Tab has no workspaceId -> buildAppUrl uses /basecamp/...; keep /{slug}/basecamp/... in the bar.
    if (basecampPathnamesEquivalent(location.pathname, targetUrl)) {
      const desired = mergeIndexQueryForPath(
        location.pathname,
        location.search || '',
        targetUrl,
        activeTab,
      )
      lastTabIdRef.current = activeTabId
      if (desired !== locationFull) {
        lastUrlRef.current = desired
        isUpdatingRef.current = true
        logUrlSync('tab-to-url', 'navigate', 'basecamp-equiv-query', {
          pathname: pathNow,
          activeTabId,
          targetUrl: desired,
        })
        syncNavigate(desired, { replace: true })
        setTimeout(() => { isUpdatingRef.current = false }, 100)
        return
      }
      lastUrlRef.current = locationFull
      return
    }

    // Keep Basecamp todo deep links when localStorage still has Home as activeTab (cold load race).
    // Skip when the user/tab system intentionally activated Home (workspace toggle, tab click).
    if (
      !tabDrivenUrlSync &&
      targetUrl === '/' &&
      (activeTab.type === 'home' || activeTab.id === 'home') &&
      /\/basecamp\/project\/\d+\/todo\/\d+/.test(location.pathname)
    ) {
      logUrlSync('tab-to-url', 'skip', 'keep-bc-todo-deep-link', {
        pathname: pathNow,
        activeTabId,
      })
      return
    }

    // Keep extensionless document deep links when Home is still the active tab (cold load race).
    if (
      !tabDrivenUrlSync &&
      targetUrl === '/' &&
      (activeTab.type === 'home' || activeTab.id === 'home') &&
      isDocumentDeepLinkPath(location.pathname, workspacesRoute)
    ) {
      logUrlSync('tab-to-url', 'skip', 'keep-doc-deep-link', {
        pathname: pathNow,
        activeTabId,
      })
      return
    }

    if (activeTab.type === 'explorer' && navigationSourceRef.current === 'programmatic') {
      lastUrlRef.current = targetUrl
      return
    }

    if (hasPendingDoc && targetUrl === '/') {
      logUrlSync('tab-to-url', 'skip', 'pending-doc-home-target', {
        pathname: pathNow,
        activeTabId,
      })
      return
    }

    // Full path+query so /search?view=activity is not collapsed to /search.
    const currentFull = `${location.pathname}${location.search || ''}`
    if (currentFull !== targetUrl && lastUrlRef.current !== targetUrl) {
      // Update refs BEFORE navigating to prevent race
      lastTabIdRef.current = activeTabId
      lastUrlRef.current = targetUrl
      isUpdatingRef.current = true
      if (activeTab.type === 'chat-conversation' || activeTab.type === 'inbox-thread') {
        markProgrammaticNavigation()
      }
      logUrlSync('tab-to-url', 'navigate', 'tab-driven', {
        pathname: pathNow,
        activeTabId,
        targetUrl,
      })
      syncNavigate(targetUrl, { replace: true })
      // Clear the updating flag after navigation completes
      setTimeout(() => { isUpdatingRef.current = false }, 100)
    }
  }, [activeTabId, syncNavigate, location.pathname, location.search, openTabs, workspacesRoute])

  // URL changed - ONLY respond to browser back/forward or direct URL entry
  // Let tabs work normally!
  useEffect(() => {
    const { projectId, todoId, listId, wsId: wsIdParam } = params
    let documentId = docIdFromPath
    const pathname = location.pathname

    syncPendingDocumentDeepLinkRef(pendingDocumentDeepLinkRef, pendingRestoreStartedRef)

    const legacyTarget = resolveLegacyPath(pathname, workspacesRoute)
    if (legacyTarget && legacyTarget !== pathname) {
      const legacyKey = `${pathname}=>${legacyTarget}`
      if (lastLegacyRedirectRef.current === legacyKey) {
        return
      }
      lastLegacyRedirectRef.current = legacyKey
      isUpdatingRef.current = true
      syncNavigate(legacyTarget, { replace: true })
      setTimeout(() => {
        isUpdatingRef.current = false
      }, 100)
      return
    }
    if (lastLegacyRedirectRef.current && pathname.includes('/basecamp/')) {
      lastLegacyRedirectRef.current = null
    }

    const urlIntent = parseAppPath(
      pathname,
      new URLSearchParams(location.search),
      workspacesRoute
    )

    if (urlIntent.type === 'legacy-redirect' && urlIntent.targetPath !== pathname) {
      isUpdatingRef.current = true
      syncNavigate(urlIntent.targetPath, { replace: true })
      setTimeout(() => {
        isUpdatingRef.current = false
      }, 100)
      return
    }

    // Activity / All edits turfed to Search (?view=activity). /edit-history redirects above.
    if (urlIntent.type === 'search') {
      if (
        navigationSourceRef.current === 'tab-click' ||
        navigationSourceRef.current === 'programmatic'
      ) {
        if (activeTabId !== 'search-view') {
          return
        }
      }
      if (activeTabId === 'search-view') {
        lastUrlRef.current = `${pathname}${location.search || ''}`
        lastTabIdRef.current = 'search-view'
        return
      }
      if (
        activeTabId &&
        activeTabId !== 'search-view' &&
        openTabs.some((t) => t.id === activeTabId)
      ) {
        return
      }
      markProgrammaticNavigation()
      isUpdatingRef.current = true
      lastUrlRef.current = `${pathname}${location.search || ''}`
      lastTabIdRef.current = 'search-view'
      onUrlChange('search-view', {
        type: 'search',
        ...(urlIntent.view ? { searchNamedView: urlIntent.view } : {}),
        ...(urlIntent.q ? { searchQuery: urlIntent.q } : {}),
        ...(urlIntent.path ? { searchPath: urlIntent.path } : {}),
        ...(urlIntent.user ? { searchUpdatedBy: urlIntent.user } : {}),
        ...(urlIntent.semantic ? { searchSemantic: true } : {}),
      })
      setTimeout(() => {
        isUpdatingRef.current = false
      }, 100)
      return
    }

    if (urlIntent.type === 'people-index') {
      if (
        navigationSourceRef.current === 'tab-click' ||
        navigationSourceRef.current === 'programmatic'
      ) {
        if (activeTabId !== 'people-index') {
          return
        }
      }
      const currentPeople = openTabs.find((tab) => tab.id === 'people-index')
      const workspaceMatches =
        !urlIntent.workspaceId
        || currentPeople?.workspaceId === urlIntent.workspaceId
      if (activeTabId === 'people-index' && workspaceMatches) {
        lastUrlRef.current = pathname
        lastTabIdRef.current = 'people-index'
        return
      }
      markProgrammaticNavigation()
      isUpdatingRef.current = true
      lastUrlRef.current = pathname
      lastTabIdRef.current = 'people-index'
      onUrlChange('people-index', {
        type: 'people-index',
        ...(urlIntent.workspaceId ? { workspaceId: urlIntent.workspaceId } : {}),
        ...(urlIntent.workspaceSlug ? { workspaceSlug: urlIntent.workspaceSlug } : {}),
      })
      setTimeout(() => {
        isUpdatingRef.current = false
      }, 100)
      return
    }

    if (urlIntent.type === 'person') {
      const personTabId = `person-view-${urlIntent.userId}`
      if (
        navigationSourceRef.current === 'tab-click' ||
        navigationSourceRef.current === 'programmatic'
      ) {
        if (activeTabId !== personTabId) {
          return
        }
      }
      if (activeTabId === personTabId) {
        lastUrlRef.current = pathname
        lastTabIdRef.current = personTabId
        return
      }
      markProgrammaticNavigation()
      isUpdatingRef.current = true
      lastUrlRef.current = pathname
      lastTabIdRef.current = personTabId
      onUrlChange(personTabId, {
        type: 'person',
        personUserId: urlIntent.userId,
        ...(urlIntent.workspaceId ? { workspaceId: urlIntent.workspaceId } : {}),
        ...(urlIntent.workspaceSlug ? { workspaceSlug: urlIntent.workspaceSlug } : {}),
      })
      setTimeout(() => {
        isUpdatingRef.current = false
      }, 100)
      return
    }

    // Bare workspace-slug URL (e.g. /kootenay-lightweb-shared) - open the workspace home view,
    // never document-tab-creation logic. Without this branch the intent fell through unhandled
    // here and (during the cold-load race) got misclassified as a document id downstream,
    // producing a 404 retry storm (Bug 4, 2026-07-08 pilot testing pass).
    //
    // Tab-click / programmatic must win: opening a doc from Recent Documents updates
    // activeTabId before the address bar catches up. Without this guard the still-stale
    // /{slug} URL re-forces workspace-home and the open appears to fail (view still tracked).
    if (urlIntent.type === 'workspace-home') {
      const wsId = workspaceIdForSlug(workspacesRoute, urlIntent.workspaceSlug)
      const tabId = wsId ? `workspace-home-${wsId}` : null
      if (
        navigationSourceRef.current === 'tab-click' ||
        navigationSourceRef.current === 'programmatic'
      ) {
        if (activeTabId === tabId) {
          lastTabIdRef.current = tabId
          lastUrlRef.current = pathname
        }
        return
      }
      if (tabId && activeTabId !== tabId) {
        isUpdatingRef.current = true
        lastTabIdRef.current = tabId
        lastUrlRef.current = pathname
        onUrlChange(tabId, { type: 'workspace-home', workspaceId: wsId })
        setTimeout(() => {
          isUpdatingRef.current = false
        }, 500)
      }
      return
    }

    if (urlIntent.type === 'flow-studio') {
      const tabId = `flow-studio:${urlIntent.surface}`
      if (activeTabId === tabId) {
        lastTabIdRef.current = tabId
        lastUrlRef.current = pathname
        return
      }
      if (
        navigationSourceRef.current === 'tab-click' ||
        navigationSourceRef.current === 'programmatic' ||
        isUpdatingRef.current
      ) {
        return
      }
      markProgrammaticNavigation()
      isUpdatingRef.current = true
      lastTabIdRef.current = tabId
      lastUrlRef.current = pathname
      window.dispatchEvent(
        new CustomEvent('flownotes:flow-open-surface', {
          detail: { surface: urlIntent.surface },
        }),
      )
      setTimeout(() => {
        isUpdatingRef.current = false
      }, 100)
      return
    }

    if (urlIntent.type === 'home') {
      const currentTab = openTabs.find((t) => t.id === activeTabId)
      const onInboxThread =
        activeTabId?.startsWith('inbox-thread-') || currentTab?.type === 'inbox-thread'

      if (onInboxThread && currentTab) {
        const targetUrl = buildAppUrl({ tab: currentTab, workspaces: workspacesRoute })
        if (targetUrl !== '/' && location.pathname !== targetUrl) {
          markProgrammaticNavigation()
          isUpdatingRef.current = true
          lastUrlRef.current = targetUrl
          lastTabIdRef.current = activeTabId
          syncNavigate(targetUrl, { replace: true })
          setTimeout(() => {
            isUpdatingRef.current = false
          }, 100)
        }
        return
      }
    }

    if (urlIntent.type === 'inbox-thread') {
      const tabId = `inbox-thread-${urlIntent.threadId}`
      const currentTab = openTabs.find((t) => t.id === activeTabId)

      if (
        navigationSourceRef.current === 'tab-click' ||
        navigationSourceRef.current === 'programmatic'
      ) {
        if (activeTabId === tabId) {
          lastTabIdRef.current = tabId
          lastUrlRef.current = pathname
        }
        return
      }

      // Stale inbox URL after close-to-home or workspace switch: bar still on /inbox/thread/...
      // but Home is active — normalize URL instead of resurrecting the thread tab.
      if (currentTab?.type === 'home' || activeTabId === 'home') {
        if (pathname !== '/') {
          isUpdatingRef.current = true
          lastUrlRef.current = '/'
          lastTabIdRef.current = 'home'
          syncNavigate('/', { replace: true })
          setTimeout(() => {
            isUpdatingRef.current = false
          }, 100)
        }
        return
      }

      if (activeTabId !== tabId) {
        isUpdatingRef.current = true
        lastTabIdRef.current = tabId
        lastUrlRef.current = pathname
        window.dispatchEvent(
          new CustomEvent('open-inbox-thread-main', {
            detail: { threadId: urlIntent.threadId },
          })
        )
        setTimeout(() => {
          isUpdatingRef.current = false
        }, 500)
      }
      return
    }

    if (urlIntent.type === 'chat-conversation') {
      const tabId = chatConversationTabId(urlIntent.threadId)
      const currentTab = openTabs.find((t) => t.id === activeTabId)

      if (
        navigationSourceRef.current === 'tab-click' ||
        navigationSourceRef.current === 'programmatic' ||
        isUpdatingRef.current
      ) {
        if (activeTabId === tabId) {
          lastTabIdRef.current = tabId
          lastUrlRef.current = pathname
        }
        return
      }

      // Active chat tab is ahead of a stale URL during rapid sidebar switches
      if (
        currentTab?.type === 'chat-conversation' &&
        currentTab.chatThreadId &&
        currentTab.chatThreadId !== urlIntent.threadId
      ) {
        return
      }

      if (currentTab?.type === 'home' || activeTabId === 'home') {
        const chatTabExists = openTabs.some((t) => t.id === tabId)
        if (chatTabExists && activeTabId !== tabId) {
          isUpdatingRef.current = true
          lastTabIdRef.current = tabId
          lastUrlRef.current = pathname
          window.dispatchEvent(
            new CustomEvent('open-chat-conversation-main', {
              detail: { threadId: urlIntent.threadId },
            }),
          )
          setTimeout(() => {
            isUpdatingRef.current = false
          }, 500)
          return
        }
        if (!chatTabExists && pathname !== '/') {
          isUpdatingRef.current = true
          lastUrlRef.current = '/'
          lastTabIdRef.current = 'home'
          syncNavigate('/', { replace: true })
          setTimeout(() => {
            isUpdatingRef.current = false
          }, 100)
        }
        return
      }

      if (activeTabId !== tabId) {
        isUpdatingRef.current = true
        lastTabIdRef.current = tabId
        lastUrlRef.current = pathname
        window.dispatchEvent(
          new CustomEvent('open-chat-conversation-main', {
            detail: { threadId: urlIntent.threadId },
          }),
        )
        setTimeout(() => {
          isUpdatingRef.current = false
        }, 500)
      }
      return
    }

    if (urlIntent.type === 'document' && !documentId) {
      documentId = urlIntent.pathOrId
    }

    const bcFromIntent =
      urlIntent.type === 'extension-basecamp'
        ? {
            workspaceId:
              urlIntent.workspaceId ??
              (urlIntent.workspaceSlug
                ? workspaceIdForSlug(workspacesRoute, urlIntent.workspaceSlug)
                : undefined),
            strippedPath: urlIntent.strippedPath,
          }
        : stripOptionalBasecampWorkspaceSegment(pathname)
    const { workspaceId: bcWorkspaceFromStrip, strippedPath: basecampCanonicalPath } =
      bcFromIntent
    const wsForBasecampUrl = wsIdParam ?? bcWorkspaceFromStrip

    // Restore document deep link once workspaces load (handles cold load race where bar briefly hits /).
    const pendingDoc = pendingDocumentDeepLinkRef.current
    if (pendingDoc) {
      if (workspacesRoute.length === 0) {
        return
      }

      if (!pendingRestoreStartedRef.current) {
        // Re-classify now that workspaces are loaded - the deferred entry was forced to
        // 'document' at defer time (slug -> workspace matching needs the workspace list, so
        // it could not be resolved yet). It may resolve to a different intent now, e.g. a
        // bare workspace-slug URL becomes 'workspace-home' once its org is in workspacesRoute.
        // Without this re-check, workspace-slug URLs deferred during the cold-load race were
        // permanently misclassified as document ids, causing a 404 retry storm and UI freeze
        // (Bug 4, 2026-07-08 pilot testing pass).
        const freshIntent = parseAppPath(
          pendingDoc.pathname,
          new URLSearchParams(location.search),
          workspacesRoute,
        )

        if (freshIntent.type === 'workspace-home') {
          clearPendingDocumentDeepLink(pendingDocumentDeepLinkRef, pendingRestoreStartedRef)
          hasInitializedRef.current = true
          const wsId = workspaceIdForSlug(workspacesRoute, freshIntent.workspaceSlug)
          if (wsId) {
            const tabId = `workspace-home-${wsId}`
            isUpdatingRef.current = true
            lastTabIdRef.current = tabId
            lastUrlRef.current = pendingDoc.pathname
            onUrlChange(tabId, { type: 'workspace-home', workspaceId: wsId })
            setTimeout(() => {
              isUpdatingRef.current = false
            }, 500)
          }
          return
        }

        pendingRestoreStartedRef.current = true
        hasInitializedRef.current = true
        const existingForPath = findOpenTabForDocumentPath(
          openTabs,
          pendingDoc.pathOrId,
          workspacesRoute,
        )
        const contentId = existingForPath?.id ?? pendingDoc.pathOrId
        // Focus target workspace before by-path / tab-sync can fall back to Home.
        const targetWsId = workspaceIdForDocumentUrlPath(
          pendingDoc.pathOrId,
          workspacesRoute,
        )
        if (
          targetWsId &&
          (activeWorkspaceIds.length !== 1 || activeWorkspaceIds[0] !== targetWsId)
        ) {
          hideOtherWorkspaces(targetWsId)
        }
        // Path provisionals use the slug as id. `/doc/{uuid}` provisionals use the UUID
        // as id and filePath. Neither is loaded yet. Clearing here skipped the fetch
        // and let Home replace the share URL. Second click worked because the app was warm.
        const alreadyOpen =
          shareLinkTabCountsAsOpen(existingForPath, activeTabId) &&
          documentUrlPathsEquivalent(pathname, pendingDoc.pathname, workspacesRoute)
        if (alreadyOpen) {
          clearPendingDocumentDeepLink(
            pendingDocumentDeepLinkRef,
            pendingRestoreStartedRef,
          )
          return
        }
        // Same cooldown guard used elsewhere in this file - avoid retrying a deep link that
        // recently 404'd (defense in depth alongside the workspace-home re-check above).
        // Extensionless paths are document URLs (ADR-0012). Do not treat them as folders
        // here; url-handlers probes .md/.html first and opens explorer only on a real miss.
        const pendingLastFailedTime = lastFailedLoadTimeRef.current.get(contentId)
        const pendingTimeSinceFailure = pendingLastFailedTime
          ? Date.now() - pendingLastFailedTime
          : Infinity
        if (
          failedDocumentLoadsRef.current.has(contentId) &&
          pendingTimeSinceFailure < 10000
        ) {
          if (contentId.includes('/')) {
            clearPendingDocumentDeepLink(pendingDocumentDeepLinkRef, pendingRestoreStartedRef)
            isUpdatingRef.current = true
            lastTabIdRef.current = 'list'
            lastUrlRef.current = pendingDoc.pathname
            onUrlChange('list', {
              type: 'explorer',
              folderPath:
                normalizeFolderStoragePath(contentId, workspacesRoute) ?? contentId,
            })
            setTimeout(() => {
              isUpdatingRef.current = false
            }, 500)
            return
          }
          clearPendingDocumentDeepLink(pendingDocumentDeepLinkRef, pendingRestoreStartedRef)
          return
        }
        isUpdatingRef.current = true
        lastTabIdRef.current = contentId
        lastUrlRef.current = pendingDoc.pathname
        if (
          !documentUrlPathsEquivalent(pathname, pendingDoc.pathname, workspacesRoute) &&
          pathname !== '/' &&
          pathname !== ''
        ) {
          syncNavigate(pendingDoc.pathname, { replace: true })
        }
        pendingDocRetryAtRef.current = Date.now()
        onUrlChange(contentId, { type: 'document' })
        setTimeout(() => {
          isUpdatingRef.current = false
        }, 500)
        return
      }

      const activeTabForPending = openTabs.find((tab) => tab.id === activeTabId)
      const tabMatchesPending = Boolean(
        activeTabForPending &&
          documentUrlPathsEquivalent(
            buildAppUrl({ tab: activeTabForPending, workspaces: workspacesRoute }),
            pendingDoc.pathname,
            workspacesRoute,
          ),
      )
      const barMatchesPending = documentUrlPathsEquivalent(
        pathname,
        pendingDoc.pathname,
        workspacesRoute,
      )

      if (
        pendingRestoreStartedRef.current &&
        tabMatchesPending &&
        !barMatchesPending &&
        pathname !== '/' &&
        pathname !== ''
      ) {
        isUpdatingRef.current = true
        lastUrlRef.current = pendingDoc.pathname
        syncNavigate(pendingDoc.pathname, { replace: true })
        setTimeout(() => {
          isUpdatingRef.current = false
        }, 100)
        return
      }

      // Path provisionals use the slug path as id. /doc/{uuid} provisionals use the
      // UUID as id *and* filePath - that is not loaded yet. Wait for a storage path.
      const pendingTabResolved = isColdLoadDocumentTabResolved(activeTabForPending)

      // Once the note has a storage path, buildAppUrl is the folder URL, so it no
      // longer matches /doc/{uuid}. The old tabMatchesPending requirement then
      // left pending set forever and the viewer kept skipping the vanity rewrite.
      const pendingDocIsThisTab =
        !!activeTabForPending &&
        (activeTabForPending.id === pendingDoc.pathOrId ||
          tabMatchesPending ||
          barMatchesPending)
      if (
        pendingTabResolved &&
        pendingDocIsThisTab &&
        activeTabId &&
        activeTabId !== 'home' &&
        activeTabForPending?.type !== 'home'
      ) {
        const vanity = buildAppUrl({
          tab: activeTabForPending,
          workspaces: workspacesRoute,
        })
        const vanityPath = vanity.split('?')[0] || vanity
        clearPendingDocumentDeepLink(
          pendingDocumentDeepLinkRef,
          pendingRestoreStartedRef,
        )
        if (pathname !== vanityPath) {
          isUpdatingRef.current = true
          lastTabIdRef.current = activeTabId
          lastUrlRef.current = vanity
          syncNavigate(vanity, { replace: true })
          setTimeout(() => {
            isUpdatingRef.current = false
          }, 100)
        }
        return
      }

      // Workspace list arrived after the first attempt no-op'd. Retry the
      // pending document only. A leftover unresolved note tab must not be
      // resolved in its place - that 404 used to cancel this restore.
      if (
        shouldRetryPendingDocumentRestore({
          tabMatchesPending,
          workspacesReady: workspacesRoute.length > 0,
          activeTabType: activeTabForPending?.type,
          pendingTabResolved,
        }) &&
        Date.now() - pendingDocRetryAtRef.current > 1200
      ) {
        pendingDocRetryAtRef.current = Date.now()
        isUpdatingRef.current = true
        lastTabIdRef.current = pendingDoc.pathOrId
        lastUrlRef.current = pendingDoc.pathname
        onUrlChange(pendingDoc.pathOrId, { type: 'document' })
        setTimeout(() => {
          isUpdatingRef.current = false
        }, 500)
      }

      return
    }

    // On initial load, check if URL doesn't match current tab state and restore
    // This handles the case where localStorage only has Home tab but URL indicates a different tab
    if (!hasInitializedRef.current) {
      // Wait for workspace list before slug -> content_root translation (ADR-0012).
      if (urlIntent.type === 'document' && workspacesRoute.length === 0) {
        const entry = {
          pathname,
          pathOrId: documentId ?? urlIntent.pathOrId,
        }
        pendingDocumentDeepLinkRef.current = entry
        setPendingDocumentDeepLink(entry)
        return
      }

      // Bar can flicker to / while workspaces load; do not finalize init as home yet.
      if (pendingDocumentDeepLinkRef.current) {
        return
      }

      hasInitializedRef.current = true

      // Extensionless document deep links (v2) vs restored localStorage tabs
      if (urlIntent.type === 'document' && documentId) {
        const existingForPath = findOpenTabForDocumentPath(
          openTabs,
          documentId,
          workspacesRoute,
        )
        const activeTabForInit = openTabs.find((tab) => tab.id === activeTabId)
        const activeUrl = activeTabForInit
          ? buildAppUrl({ tab: activeTabForInit, workspaces: workspacesRoute })
          : '/'
        if (!documentUrlPathsEquivalent(pathname, activeUrl, workspacesRoute)) {
          isUpdatingRef.current = true
          lastTabIdRef.current = existingForPath?.id ?? documentId
          lastUrlRef.current = pathname
          onUrlChange(existingForPath?.id ?? documentId, { type: 'document' })
          setTimeout(() => {
            isUpdatingRef.current = false
          }, 500)
          return
        }
      }

      const workspaceHomeInit = pathname.match(/^\/workspace\/([^/]+)$/)
      if (workspaceHomeInit) {
        const wsDecoded = decodeURIComponent(workspaceHomeInit[1])
        const tabId = `workspace-home-${wsDecoded}`
        if (!openTabs.some(tab => tab.id === tabId)) {
          isUpdatingRef.current = true
          lastTabIdRef.current = tabId
          lastUrlRef.current = pathname
          onUrlChange(tabId, { type: 'workspace-home', workspaceId: wsDecoded })
          setTimeout(() => { isUpdatingRef.current = false }, 500)
          return
        }
      }

      // Check if URL indicates Basecamp home (no project)
      if (pathname === '/basecamp' || pathname === '/basecamp/') {
        const basecampHomeId = 'basecamp'
        // If any integration-basecamp tab is already open (e.g. a project tab from localStorage),
        // don't override it — the URL just means "go to basecamp", not "open the home tab"
        const hasAnyBasecampTab = openTabs.some(tab => tab.type === 'integration-basecamp' || tab.id === basecampHomeId)
        if (!hasAnyBasecampTab) {
          isUpdatingRef.current = true
          lastTabIdRef.current = basecampHomeId
          lastUrlRef.current = pathname
          onUrlChange(basecampHomeId, { type: 'integration-basecamp' })
          setTimeout(() => { isUpdatingRef.current = false }, 500)
          return
        }
      }

      // Workspace-scoped Basecamp home: /basecamp/{workspaceId}
      if (
        pathname.startsWith('/basecamp') &&
        bcWorkspaceFromStrip &&
        (basecampCanonicalPath === '/basecamp' || basecampCanonicalPath === '/basecamp/')
      ) {
        const basecampHomeId = 'basecamp'
        const already = openTabs.some(
          (tab) =>
            tab.id === basecampHomeId &&
            tab.type === 'integration-basecamp' &&
            tab.workspaceId === bcWorkspaceFromStrip
        )
        if (!already) {
          isUpdatingRef.current = true
          lastTabIdRef.current = basecampHomeId
          lastUrlRef.current = pathname
          onUrlChange(basecampHomeId, {
            type: 'integration-basecamp',
            workspaceId: bcWorkspaceFromStrip,
          })
          setTimeout(() => { isUpdatingRef.current = false }, 500)
          return
        }
      }

      // Check if URL indicates a Basecamp section view
      const basecampSectionMatch = basecampCanonicalPath.match(
        /^\/basecamp\/(bookmarks|mystuff|todos|projects)$/
      )
      if (basecampSectionMatch) {
        const section = basecampSectionMatch[1] as 'bookmarks' | 'mystuff' | 'todos' | 'projects'
        const sectionTabId = `basecamp-${section}`
        if (!openTabs.some(tab => tab.id === sectionTabId)) {
          isUpdatingRef.current = true
          lastTabIdRef.current = sectionTabId
          lastUrlRef.current = pathname
          onUrlChange(sectionTabId, {
            type: 'integration-basecamp',
            section,
            ...basecampWorkspaceParams(urlIntent, wsForBasecampUrl),
          })
          setTimeout(() => { isUpdatingRef.current = false }, 500)
          return
        }
      }

      // Check if URL indicates Basecamp triage
      if (pathname === '/basecamp/triage') {
        const triageTabId = 'basecamp-triage'
        if (!openTabs.some(tab => tab.id === triageTabId)) {
          isUpdatingRef.current = true
          lastTabIdRef.current = triageTabId
          lastUrlRef.current = pathname
          onUrlChange(triageTabId, { type: 'integration-basecamp-triage' })
          setTimeout(() => { isUpdatingRef.current = false }, 500)
          return
        }
      }

      const basecampTodoListInit = basecampCanonicalPath.match(
        /^\/basecamp\/project\/(\d+)\/todo-list\/(\d+)$/
      )
      if (basecampTodoListInit) {
        const urlProjectId = basecampTodoListInit[1]
        const urlListId = basecampTodoListInit[2]
        const expectedTabId = `basecamp-todo-list-${urlListId}`
        if (!openTabs.some(tab => tab.id === expectedTabId)) {
          isUpdatingRef.current = true
          lastTabIdRef.current = expectedTabId
          lastUrlRef.current = pathname
          onUrlChange(expectedTabId, {
            type: 'integration-basecamp-todo-list',
            projectId: urlProjectId,
            listId: urlListId,
            ...basecampWorkspaceParams(urlIntent, wsForBasecampUrl),
          })
          setTimeout(() => { isUpdatingRef.current = false }, 500)
          return
        }
      }

      // Check if URL indicates a Basecamp project but we don't have that tab
      const basecampMatch = basecampCanonicalPath.match(
        /^\/basecamp\/project\/(\d+)(?:\/todo\/(\d+))?$/
      )
      if (basecampMatch) {
        const urlProjectId = basecampMatch[1]
        const urlTodoId = basecampMatch[2]
        const expectedTabId = urlTodoId ? `basecamp-todo-${urlTodoId}` : `basecamp-project-${urlProjectId}`

        // If we don't have this tab, restore it
        if (!openTabs.some(tab => tab.id === expectedTabId)) {
          isUpdatingRef.current = true
          lastTabIdRef.current = expectedTabId
          lastUrlRef.current = pathname

          if (urlTodoId) {
            onUrlChange(expectedTabId, {
              type: 'integration-basecamp-todo',
              projectId: urlProjectId,
              todoId: urlTodoId,
              ...basecampWorkspaceParams(urlIntent, wsForBasecampUrl),
            })
          } else {
            onUrlChange(expectedTabId, {
              type: 'integration-basecamp',
              projectId: urlProjectId,
              ...basecampWorkspaceParams(urlIntent, wsForBasecampUrl),
            })
          }
          setTimeout(() => { isUpdatingRef.current = false }, 500)
          return
        }
      }

      // Check if URL indicates a document but we don't have that tab
      const docMatch = pathname.match(/^\/doc\/(.+)$/)
      if (docMatch) {
        const urlDocumentId = decodeURIComponent(docMatch[1])
        if (!openTabs.some(tab => tab.id === urlDocumentId)) {
          // Check if this document recently failed to load
          const lastFailedTime = lastFailedLoadTimeRef.current.get(urlDocumentId)
          const timeSinceLastFailure = lastFailedTime ? Date.now() - lastFailedTime : Infinity
          if (failedDocumentLoadsRef.current.has(urlDocumentId) && timeSinceLastFailure < 10000) {
            console.warn('[URL Sync] Initial load: Skipping document with recent failure:', {
              documentId: urlDocumentId,
              timeSinceLastFailure: Math.round(timeSinceLastFailure / 1000) + 's'
            })
            if (!isDocumentDeepLinkPath(pathname, workspacesRoute)) {
              lastUrlRef.current = '/'
              syncNavigate('/', { replace: true })
            }
            return
          }

          isUpdatingRef.current = true
          lastTabIdRef.current = urlDocumentId
          lastUrlRef.current = pathname
          onUrlChange(urlDocumentId, { type: 'document' })
          setTimeout(() => { isUpdatingRef.current = false }, 500)
          return
        }
      }

      // Check if URL indicates Explorer but we don't have that tab
      const exploreMatch = pathname.match(/^\/explore(?:\/(.+))?$/)
      if (exploreMatch) {
        const urlFolderPath = exploreMatch[1] ? decodeURIComponent(exploreMatch[1]) : null
        const explorerTab = openTabs.find(tab => tab.type === 'explorer')
        if (!explorerTab || explorerTab.explorerData?.folderPath !== urlFolderPath) {
          isUpdatingRef.current = true
          lastTabIdRef.current = 'list'
          lastUrlRef.current = pathname
          onUrlChange('list', {
            type: 'explorer',
            folderPath: urlFolderPath
          })
          setTimeout(() => { isUpdatingRef.current = false }, 500)
          return
        }
      }
    }

    // CRITICAL FIX: Early return for home tab at root to prevent loops
    const currentTab = openTabs.find(tab => tab.id === activeTabId)
    if (location.pathname === '/' && currentTab?.type === 'home') {
      // Update tracking silently and return immediately
      if (lastUrlRef.current !== '/' || lastTabIdRef.current !== activeTabId) {
        lastUrlRef.current = '/'
        lastTabIdRef.current = activeTabId
      }
      return
    }

    // Address bar still /doc/... but active tab is a route-owned integration (e.g. Product Board replaced the doc slot).
    // Must run before "pathname === lastUrlRef" early return, which would otherwise skip all sync while URL stays stale.
    if (documentId && activeTabId && location.pathname.startsWith('/doc/')) {
      const decodedDocId = documentId
      const activeTabForUrl = openTabs.find((tab) => tab.id === activeTabId)
      if (activeTabForUrl?.type === 'integration-product-board' && activeTabId !== decodedDocId) {
        if (!isUpdatingRef.current) {
          markProgrammaticNavigation()
          isUpdatingRef.current = true
          lastUrlRef.current = '/product-board'
          lastTabIdRef.current = activeTabId
          syncNavigate('/product-board', { replace: true })
          setTimeout(() => {
            isUpdatingRef.current = false
          }, 100)
        }
        return
      }
      if (activeTabForUrl?.type === 'integration-voice-memos' && activeTabId !== decodedDocId) {
        if (!isUpdatingRef.current) {
          markProgrammaticNavigation()
          isUpdatingRef.current = true
          lastUrlRef.current = '/voice-memos'
          lastTabIdRef.current = activeTabId
          syncNavigate('/voice-memos', { replace: true })
          setTimeout(() => {
            isUpdatingRef.current = false
          }, 100)
        }
        return
      }
    }

    // Skip if this is the URL we just set (from normal navigation).
    // Exception only while a cold document deep link is still pending (lastUrlRef can
    // be set during async createNewTab before activeTabId updates). A settled mismatch
    // must NOT re-dispatch: tab reorder changes openTabs, which re-runs this effect,
    // and re-opening the document URL fights the other tab forever.
    if (location.pathname === lastUrlRef.current && !isDocumentDeepLinkRestorePending()) {
      logUrlSync('url-to-tab', 'skip', 'same-url', {
        pathname: location.pathname,
        activeTabId,
      })
      return
    }
    if (location.pathname === lastUrlRef.current) {
      const searchParamsForDefer = new URLSearchParams(location.search)
      const activeTabForDefer = openTabs.find((tab) => tab.id === activeTabId)
      if (
        !shouldDeferTabUrlSyncToAddressBar(
          location.pathname,
          searchParamsForDefer,
          workspacesRoute,
          activeTabForDefer,
        )
      ) {
        logUrlSync('url-to-tab', 'skip', 'same-url-no-defer', {
          pathname: location.pathname,
          activeTabId,
        })
        return
      }
      logUrlSync('url-to-tab', 'skip', 'same-url-pending-defer', {
        pathname: location.pathname,
        activeTabId,
      })
    }

    // Skip if we're already updating
    if (isUpdatingRef.current) {
      return
    }

    // CRITICAL FIX: Rate limiting - prevent rapid-fire navigation attempts
    const MIN_NAVIGATION_INTERVAL = 1000 // 1 second minimum between navigation attempts
    const lastAttempt = lastNavigationAttemptRef.current.get(location.pathname) || 0
    const timeSinceLastAttempt = Date.now() - lastAttempt
    if (timeSinceLastAttempt < MIN_NAVIGATION_INTERVAL) {
      logUrlSync('url-to-tab', 'skip', 'rate-limit', {
        pathname: location.pathname,
        activeTabId,
      })
      return
    }
    lastNavigationAttemptRef.current.set(location.pathname, Date.now())

    // CRITICAL FIX: Skip if this URL change was from a tab click or programmatic navigation
    // Tab clicks update URL after document loads, so ignore the URL change event
    // Programmatic navigation (folder clicks) already updated state, so ignore URL change
    if (navigationSourceRef.current === 'tab-click' || navigationSourceRef.current === 'programmatic') {
      logUrlSync('url-to-tab', 'skip', `source-${navigationSourceRef.current}`, {
        pathname: location.pathname,
        activeTabId,
      })
      return
    }

    // /workspace/{workspaceId} - workspace home dashboard
    const workspacePathMatch = pathname.match(/^\/workspace\/([^/]+)$/)
    if (workspacePathMatch) {
      const wsDecoded = decodeURIComponent(workspacePathMatch[1])
      const tabId = `workspace-home-${wsDecoded}`
      if (activeTabId !== tabId) {
        isUpdatingRef.current = true
        lastTabIdRef.current = tabId
        lastUrlRef.current = pathname
        onUrlChange(tabId, { type: 'workspace-home', workspaceId: wsDecoded })
        setTimeout(() => { isUpdatingRef.current = false }, 500)
      }
      return
    }

    // Handle Basecamp home route (legacy - no workspace segment)
    if (pathname === '/basecamp' || pathname === '/basecamp/') {
      const basecampHomeId = 'basecamp'
      if (activeTabId !== basecampHomeId) {
        isUpdatingRef.current = true
        lastTabIdRef.current = basecampHomeId
        lastUrlRef.current = location.pathname
        onUrlChange(basecampHomeId, { type: 'integration-basecamp' })
        setTimeout(() => { isUpdatingRef.current = false }, 500)
      }
      return
    }

    // /basecamp/{workspaceId} scoped home (canonical path is /basecamp only)
    if (
      pathname.startsWith('/basecamp') &&
      wsForBasecampUrl &&
      (basecampCanonicalPath === '/basecamp' || basecampCanonicalPath === '/basecamp/')
    ) {
      const basecampHomeId = 'basecamp'
      const bcTab = openTabs.find((t) => t.id === basecampHomeId)
      const needsWs =
        activeTabId !== basecampHomeId ||
        bcTab?.workspaceId !== wsForBasecampUrl ||
        bcTab?.integrationData?.organizationId !== wsForBasecampUrl
      if (needsWs) {
        isUpdatingRef.current = true
        lastTabIdRef.current = basecampHomeId
        lastUrlRef.current = pathname
        onUrlChange(basecampHomeId, {
          type: 'integration-basecamp',
          workspaceId: wsForBasecampUrl,
        })
        setTimeout(() => {
          isUpdatingRef.current = false
        }, 500)
      }
      return
    }

    // Handle Basecamp triage route
    if (pathname === '/basecamp/triage') {
      const triageTabId = 'basecamp-triage'
      if (activeTabId !== triageTabId) {
        isUpdatingRef.current = true
        lastTabIdRef.current = triageTabId
        lastUrlRef.current = location.pathname
        onUrlChange(triageTabId, { type: 'integration-basecamp-triage' })
        setTimeout(() => { isUpdatingRef.current = false }, 500)
      }
      return
    }

    // Handle Basecamp section routes (bookmarks, mystuff, todos, projects)
    const sectionMatch = basecampCanonicalPath.match(
      /^\/basecamp\/(bookmarks|mystuff|todos|projects)$/
    )
    if (sectionMatch) {
      const section = sectionMatch[1] as 'bookmarks' | 'mystuff' | 'todos' | 'projects'
      const sectionTabId = `basecamp-${section}`
      if (activeTabId !== sectionTabId) {
        isUpdatingRef.current = true
        lastTabIdRef.current = sectionTabId
        lastUrlRef.current = location.pathname
        logUrlSync('url-to-tab', 'onUrlChange', `basecamp-${section}`, {
          pathname: location.pathname,
          activeTabId,
          targetUrl: sectionTabId,
        })
        onUrlChange(sectionTabId, {
          type: 'integration-basecamp',
          section,
          ...basecampWorkspaceParams(urlIntent, wsForBasecampUrl),
        })
        setTimeout(() => {
          isUpdatingRef.current = false
        }, 500)
      }
      return
    }

    // Handle Basecamp todo list route (more specific than todo)
    // Only process if we have both projectId and listId from URL params
    if (projectId && listId && !todoId) {
      const basecampTodoListId = `basecamp-todo-list-${listId}`
      if (activeTabId !== basecampTodoListId) {
        // Update tracking
        isUpdatingRef.current = true
        lastTabIdRef.current = basecampTodoListId
        lastUrlRef.current = location.pathname

        onUrlChange(basecampTodoListId, {
          type: 'integration-basecamp-todo-list',
          projectId,
          listId,
          ...basecampWorkspaceParams(urlIntent, wsForBasecampUrl),
        })
        setTimeout(() => {
          isUpdatingRef.current = false
        }, 500)
      }
      return
    }

    // Bar at `/` while a Basecamp integration tab is active — restore deep link (avoids snap to Home).
    if (location.pathname === '/' && activeTabId?.startsWith('basecamp-todo-')) {
      const currentTab = openTabs.find((tab) => tab.id === activeTabId)
      if (currentTab?.type === 'integration-basecamp-todo') {
        const projectIdLoop = currentTab.integrationData?.projectId
        const todoIdLoop =
          currentTab.integrationData?.todoId || activeTabId.replace('basecamp-todo-', '')
        if (projectIdLoop && todoIdLoop) {
          const targetUrl = buildAppUrl({ tab: currentTab, workspaces: workspacesRoute })
          if (targetUrl !== '/') {
            lastUrlRef.current = targetUrl
            lastTabIdRef.current = activeTabId
            isUpdatingRef.current = true
            syncNavigate(targetUrl, { replace: true })
            setTimeout(() => {
              isUpdatingRef.current = false
            }, 100)
          }
        }
      }
      return
    }

    // If URL is `/` but we have a Basecamp todo list tab active, don't try to sync
    // This prevents loops when the tab exists but URL hasn't been set yet
    if (location.pathname === '/' && activeTabId?.startsWith('basecamp-todo-list-')) {
      const currentTab = openTabs.find(tab => tab.id === activeTabId)
      if (currentTab?.type === 'integration-basecamp-todo-list') {
        // Tab exists but URL is wrong - update URL to match tab
        const projectIdLoop = currentTab.integrationData?.projectId
        const listIdLoop = currentTab.integrationData?.listId || activeTabId.replace('basecamp-todo-list-', '')
        if (projectIdLoop && listIdLoop) {
          const targetUrl = buildAppUrl({ tab: currentTab, workspaces: workspacesRoute })
          if (lastUrlRef.current !== targetUrl) {
            lastUrlRef.current = targetUrl
            lastTabIdRef.current = activeTabId
            isUpdatingRef.current = true
            syncNavigate(targetUrl, { replace: true })
            setTimeout(() => {
              isUpdatingRef.current = false
            }, 100)
          }
        }
      }
      return
    }

    // Handle Basecamp todo route
    if (projectId && todoId) {
      const basecampTodoId = `basecamp-todo-${todoId}`
      const existingBcTab = openTabs.find((t) => t.id === basecampTodoId)
      if (existingBcTab && activeTabId === 'home') {
        isUpdatingRef.current = true
        lastTabIdRef.current = basecampTodoId
        lastUrlRef.current = location.pathname
        void Promise.resolve(
          onUrlChange(basecampTodoId, {
            type: 'integration-basecamp-todo',
            projectId,
            todoId,
            ...basecampWorkspaceParams(urlIntent, wsForBasecampUrl),
          })
        ).finally(() => {
          setTimeout(() => {
            isUpdatingRef.current = false
          }, 100)
        })
        return
      }
      if (activeTabId !== basecampTodoId) {
        // Update tracking
        isUpdatingRef.current = true
        lastTabIdRef.current = basecampTodoId
        lastUrlRef.current = location.pathname

        onUrlChange(basecampTodoId, {
          type: 'integration-basecamp-todo',
          projectId,
          todoId,
          ...basecampWorkspaceParams(urlIntent, wsForBasecampUrl),
        })
        setTimeout(() => {
          isUpdatingRef.current = false
        }, 500)
      }
      return
    }

    // Handle document routes
    if (documentId) {
      const decodedDocId = documentId // already decoded from pathname regex
      const existingForPath = findOpenTabForDocumentPath(
        openTabs,
        decodedDocId,
        workspacesRoute,
      )
      const targetTabId = existingForPath?.id ?? decodedDocId

      // If already active, just update tracking (no reload)
      if (activeTabId === targetTabId) {
        lastUrlRef.current = location.pathname
        lastTabIdRef.current = targetTabId
        // Clear failed load tracking if document is now active
        failedDocumentLoadsRef.current.delete(decodedDocId)
        failedDocumentLoadsRef.current.delete(targetTabId)
        lastFailedLoadTimeRef.current.delete(decodedDocId)
        lastFailedLoadTimeRef.current.delete(targetTabId)
        return
      }

      // CRITICAL FIX: Prevent redirect loops from failed document loads
      // If we recently tried to load this document and it failed, don't retry immediately
      const lastFailedTime = lastFailedLoadTimeRef.current.get(decodedDocId)
      const timeSinceLastFailure = lastFailedTime ? Date.now() - lastFailedTime : Infinity
      const COOLDOWN_PERIOD = 10000 // Increased to 10 seconds

      if (failedDocumentLoadsRef.current.has(decodedDocId) && timeSinceLastFailure < COOLDOWN_PERIOD) {
        console.warn('[URL Sync] BLOCKING document load - recent failure detected (preventing redirect loop):', {
          documentId: decodedDocId,
          timeSinceLastFailure: Math.round(timeSinceLastFailure / 1000) + 's',
          activeTabId,
          cooldownRemaining: Math.round((COOLDOWN_PERIOD - timeSinceLastFailure) / 1000) + 's'
        })

        // CRITICAL: Update URL to match current state to break the loop
        const tabExists = openTabs.some(tab => tab.id === decodedDocId)
        if (!tabExists && activeTabId) {
        // Tab doesn't exist - update URL to match current active tab (unless bar is a doc deep link).
        if (isDocumentDeepLinkPath(location.pathname, workspacesRoute)) {
          lastUrlRef.current = location.pathname
          return
        }
        const currentTab = openTabs.find(tab => tab.id === activeTabId)
        if (currentTab) {
            if (
              currentTab.type === 'work-spec' ||
              currentTab.type === 'note' ||
              currentTab.type === 'voice-memo' ||
              currentTab.filePath ||
              currentTab.id.includes('/')
            ) {
              const targetUrl = buildAppUrl({ tab: currentTab, workspaces: workspacesRoute })
              if (location.pathname !== targetUrl) {
                lastUrlRef.current = targetUrl
                lastTabIdRef.current = activeTabId
                syncNavigate(targetUrl, { replace: true })
              }
            } else if (currentTab.type === 'explorer') {
              // Explorer tab - go to root
              lastUrlRef.current = '/'
              lastTabIdRef.current = activeTabId
              syncNavigate('/', { replace: true })
            } else {
              // Other tab types - try to construct appropriate URL
              lastUrlRef.current = '/'
              lastTabIdRef.current = activeTabId
              syncNavigate('/', { replace: true })
            }
          } else {
            // No current tab - go to root
            lastUrlRef.current = '/'
            lastTabIdRef.current = null
            syncNavigate('/', { replace: true })
          }
        } else if (tabExists && activeTabId !== decodedDocId) {
          // Tab exists but isn't active - switch to it without loading
          lastUrlRef.current = location.pathname
          lastTabIdRef.current = decodedDocId
          // Just switch the active tab, don't trigger onUrlChange (which would try to load)
          return
        }
        return
      }

      // CRITICAL FIX: Check if tab exists before re-opening
      // If tab doesn't exist and we're on Explorer view, tab was just closed
      const tabExists = openTabs.some(tab => tab.id === decodedDocId)
      const currentTab = openTabs.find(tab => tab.id === activeTabId)
      if (
        !tabExists &&
        currentTab?.type === 'explorer' &&
        !isDocumentDeepLinkPath(location.pathname, workspacesRoute)
      ) {
        // Tab was closed, update URL to root and don't re-open.
        // Document deep links stay: extensionless paths must resolve via by-path,
        // even when Explorer is the active tab.
        lastUrlRef.current = '/'
        lastTabIdRef.current = activeTabId
        syncNavigate('/', { replace: true })
        return
      }

      // Browser navigation - either switch to existing tab or load new one
      // But first check if we should skip due to recent failure
      const shouldSkip = failedDocumentLoadsRef.current.has(decodedDocId) &&
                        (lastFailedLoadTimeRef.current.get(decodedDocId) || 0) > Date.now() - 10000

      if (shouldSkip) {
        console.warn('[URL Sync] BLOCKING browser navigation - document has recent failure:', {
          documentId: decodedDocId,
          activeTabId
        })
        // Update URL to match current state to break loop (keep document deep links in the bar).
        if (isDocumentDeepLinkPath(location.pathname, workspacesRoute)) {
          lastUrlRef.current = location.pathname
          return
        }
        if (activeTabId && activeTabId !== decodedDocId) {
          const currentTab = openTabs.find(tab => tab.id === activeTabId)
          if (currentTab) {
            const targetUrl = buildAppUrl({ tab: currentTab, workspaces: workspacesRoute })
            lastUrlRef.current = targetUrl
            lastTabIdRef.current = activeTabId
            syncNavigate(targetUrl, { replace: true })
          } else {
            lastUrlRef.current = '/'
            lastTabIdRef.current = activeTabId
            syncNavigate('/', { replace: true })
          }
        }
        return
      }

      isUpdatingRef.current = true
      lastTabIdRef.current = decodedDocId
      lastUrlRef.current = location.pathname
      // Warm click of a document URL while Home, an inbox thread, or a chat is
      // still active: treat as a deep-link restore so tab-to-URL cannot write the
      // reader URL back mid-fetch.
      if (
        tabYieldsToDocumentDeepLink(activeTabId, currentTab) &&
        !isDocumentDeepLinkRestorePending()
      ) {
        const entry = {
          pathname: location.pathname,
          pathOrId: decodedDocId,
        }
        pendingDocumentDeepLinkRef.current = entry
        setPendingDocumentDeepLink(entry)
      }
      logUrlSync('url-to-tab', 'onUrlChange', 'document', {
        pathname: location.pathname,
        activeTabId,
        targetUrl: decodedDocId,
      })
      // Let handleUrlChange decide if it needs to switch or create
      onUrlChange(decodedDocId, { type: 'document' })
      setTimeout(() => { isUpdatingRef.current = false }, 500)
    } else if (projectId) {
      // Basecamp project route (without todo)
      const basecampId = `basecamp-project-${projectId}`
      if (activeTabId !== basecampId) {
        // Update tracking
        isUpdatingRef.current = true
        lastTabIdRef.current = basecampId
        lastUrlRef.current = location.pathname

        onUrlChange(basecampId, {
          type: 'integration-basecamp',
          projectId,
          ...basecampWorkspaceParams(urlIntent, wsForBasecampUrl),
        })
        setTimeout(() => { isUpdatingRef.current = false }, 500)
      }
    } else if (location.pathname.startsWith('/explore')) {
      // Explorer route
      const pathMatch = location.pathname.match(/^\/explore(?:\/(.+))?$/)
      const folderPath = pathMatch?.[1] ? decodeURIComponent(pathMatch[1]) : null

      // Find Explorer tab
      const explorerTab = openTabs.find(tab => tab.type === 'explorer')
      const explorerTabId = explorerTab?.id || 'list'

      // Check if folder path matches current state
      const currentFolderPath = explorerTab?.explorerData?.folderPath || null
      const needsUpdate = currentFolderPath !== folderPath || activeTabId !== explorerTabId

      // Skip if this is programmatic navigation
      // Type assertion needed because TypeScript narrows type after earlier checks
      if ((navigationSourceRef.current as NavigationSource) === 'programmatic') {
        // Update tracking but don't trigger onUrlChange
        lastUrlRef.current = location.pathname
        return
      }

      if (needsUpdate) {
        isUpdatingRef.current = true
        lastTabIdRef.current = explorerTabId
        lastUrlRef.current = location.pathname

        onUrlChange(explorerTabId, {
          type: 'explorer',
          folderPath
        })
        setTimeout(() => { isUpdatingRef.current = false }, 500)
      }
    } else if (location.pathname === '/voice-memos') {
      const voiceMemoTabId = 'integration-voice-memos'
      if (activeTabId !== voiceMemoTabId) {
        isUpdatingRef.current = true
        lastTabIdRef.current = voiceMemoTabId
        lastUrlRef.current = location.pathname

        onUrlChange(voiceMemoTabId, {
          type: 'integration-voice-memos',
          integrationId: 'voice-memos'
        })
        setTimeout(() => { isUpdatingRef.current = false }, 500)
      } else {
        // Tab is already active, just update tracking to prevent loops
        lastUrlRef.current = location.pathname
        lastTabIdRef.current = voiceMemoTabId
      }
      return
    } else if (location.pathname === '/nextcloud') {
      const nextcloudTabId = 'integration-nextcloud'
      if (activeTabId === nextcloudTabId) {
        lastUrlRef.current = location.pathname
        lastTabIdRef.current = nextcloudTabId
        return
      }
      isUpdatingRef.current = true
      lastTabIdRef.current = nextcloudTabId
      lastUrlRef.current = location.pathname
      onUrlChange(nextcloudTabId, {
        type: 'integration-nextcloud',
        integrationId: 'nextcloud',
      })
      setTimeout(() => { isUpdatingRef.current = false }, 500)
      return
    } else if (location.pathname === '/drive') {
      const driveTabId = 'integration-google-drive'
      if (activeTabId === driveTabId) {
        lastUrlRef.current = location.pathname
        lastTabIdRef.current = driveTabId
        return
      }
      isUpdatingRef.current = true
      lastTabIdRef.current = driveTabId
      lastUrlRef.current = location.pathname
      onUrlChange(driveTabId, {
        type: 'integration-google-drive',
        integrationId: 'google-drive',
      })
      setTimeout(() => { isUpdatingRef.current = false }, 500)
      return
    } else if (location.pathname.startsWith('/drive/preview/')) {
      const fileId = decodeURIComponent(location.pathname.slice('/drive/preview/'.length).split('/')[0] || '')
      if (!fileId) return
      const driveTabId = `gdrive-preview-${fileId}`
      if (activeTabId === driveTabId) {
        lastUrlRef.current = location.pathname
        lastTabIdRef.current = driveTabId
        return
      }
      isUpdatingRef.current = true
      lastTabIdRef.current = driveTabId
      lastUrlRef.current = location.pathname
      onUrlChange(driveTabId, {
        type: 'integration-google-drive-preview',
        driveFileId: fileId,
        integrationId: 'google-drive',
      })
      setTimeout(() => { isUpdatingRef.current = false }, 500)
      return
    } else if (location.pathname === '/product-board') {
      const productBoardTabId = 'integration-product-board'
      if (activeTabId === productBoardTabId) {
        lastUrlRef.current = location.pathname
        lastTabIdRef.current = productBoardTabId
        return
      }
      const targetUrl = pathToUrl(DEFAULT_PRODUCT_BOARD_DOCUMENT_FILE_PATH)
      isUpdatingRef.current = true
      lastTabIdRef.current = DEFAULT_PRODUCT_BOARD_DOCUMENT_FILE_PATH
      lastUrlRef.current = targetUrl
      syncNavigate(targetUrl, { replace: true })
      onUrlChange(DEFAULT_PRODUCT_BOARD_DOCUMENT_FILE_PATH, { type: 'document' })
      setTimeout(() => { isUpdatingRef.current = false }, 500)
      return
    } else if (location.pathname === '/') {
      const currentTab = openTabs.find(tab => tab.id === activeTabId)

      if (isUpdatingRef.current && lastUrlRef.current !== '/') {
        return
      }

      if (currentTab?.type === 'home') {
        lastUrlRef.current = location.pathname
        lastTabIdRef.current = activeTabId
        return
      }

      if (currentTab) {
        const targetUrl = buildAppUrl({ tab: currentTab, workspaces: workspacesRoute })
        if (targetUrl !== '/' && location.pathname !== targetUrl) {
          isUpdatingRef.current = true
          lastUrlRef.current = targetUrl
          lastTabIdRef.current = activeTabId
          syncNavigate(targetUrl, { replace: true })
          setTimeout(() => {
            isUpdatingRef.current = false
          }, 100)
          return
        }
        lastUrlRef.current = location.pathname
        lastTabIdRef.current = activeTabId
        return
      }

      const explorerTab = openTabs.find(tab => tab.type === 'explorer')
      if (explorerTab) {
        const targetUrl = buildAppUrl({ tab: explorerTab, workspaces: workspacesRoute })
        lastUrlRef.current = targetUrl
        syncNavigate(targetUrl, { replace: true })
      }
    } else if (
      urlIntent.type === 'document' &&
      !pathname.startsWith('/doc/') &&
      documentId
    ) {
      const decodedDocId = documentId
      // Explorer browse URLs are intentionally document-shaped (ADR-0012). Do not
      // re-open as a document while an explorer tab is already at that folder, or
      // during programmatic folder/breadcrumb navigation.
      const explorerAtUrlFolder = openTabs.find((t) => {
        if (t.type !== 'explorer') return false
        const fp = String(t.explorerData?.folderPath || '')
          .replace(/^\/+/, '')
          .replace(/\/+$/, '')
        const norm = normalizeFolderStoragePath(fp, workspacesRoute) ?? fp
        const want =
          normalizeFolderStoragePath(decodedDocId, workspacesRoute) ??
          decodedDocId.replace(/^\/+|\/+$/g, '')
        return norm === want || fp === decodedDocId.replace(/^\/+|\/+$/g, '')
      })
      if (explorerAtUrlFolder) {
        if (activeTabId !== explorerAtUrlFolder.id) {
          isUpdatingRef.current = true
          lastTabIdRef.current = explorerAtUrlFolder.id
          lastUrlRef.current = pathname
          onUrlChange(explorerAtUrlFolder.id, {
            type: 'explorer',
            folderPath:
              explorerAtUrlFolder.explorerData?.folderPath ??
              normalizeFolderStoragePath(decodedDocId, workspacesRoute) ??
              decodedDocId,
          })
          setTimeout(() => {
            isUpdatingRef.current = false
          }, 500)
          return
        }
        lastUrlRef.current = pathname
        lastTabIdRef.current = explorerAtUrlFolder.id
        return
      }
      const activeTabForDocPath = openTabs.find((tab) => tab.id === activeTabId)
      if (activeTabForDocPath?.type === 'explorer') {
        const explorerFolder = String(activeTabForDocPath.explorerData?.folderPath || '')
          .replace(/^\/+/, '')
          .replace(/\/+$/, '')
        const asFolder = decodedDocId.replace(/^\/+/, '').replace(/\/+$/, '')
        const explorerNorm =
          normalizeFolderStoragePath(explorerFolder, workspacesRoute) ?? explorerFolder
        const asFolderNorm =
          normalizeFolderStoragePath(asFolder, workspacesRoute) ?? asFolder
        // Slug URL (`rowan-alice-space/home`) vs storage folder (`ws/{hex}/home`) must match.
        if (
          explorerNorm === asFolderNorm ||
          explorerFolder === asFolder ||
          (navigationSourceRef.current as NavigationSource) === 'programmatic'
        ) {
          lastUrlRef.current = pathname
          lastTabIdRef.current = activeTabId
          return
        }
      }
      if ((navigationSourceRef.current as NavigationSource) === 'programmatic') {
        lastUrlRef.current = pathname
        return
      }
      const basecampShorthand = resolveBasecampTabFromShorthandPath(decodedDocId, workspacesRoute)
      if (basecampShorthand) {
        const sectionTabId = basecampShorthand.tabId
        if (activeTabId !== sectionTabId && !openTabs.some((t) => t.id === sectionTabId)) {
          isUpdatingRef.current = true
          lastTabIdRef.current = sectionTabId
          lastUrlRef.current = pathname
          if (sectionTabId === 'basecamp-triage') {
            onUrlChange(sectionTabId, { type: 'integration-basecamp-triage' })
          } else {
            onUrlChange(sectionTabId, {
              type: 'integration-basecamp',
              section: basecampShorthand.meta.section as 'bookmarks' | 'mystuff' | 'todos' | 'projects' | undefined,
              ...(basecampShorthand.workspaceSlug
                ? { workspaceSlug: basecampShorthand.workspaceSlug }
                : {}),
              ...(basecampShorthand.workspaceId ? { workspaceId: basecampShorthand.workspaceId } : {}),
            })
          }
          setTimeout(() => {
            isUpdatingRef.current = false
          }, 500)
        }
        return
      }
      const existingForPath = findOpenTabForDocumentPath(
        openTabs,
        decodedDocId,
        workspacesRoute,
      )
      // Do not reopen a path-keyed provisional note while an explorer tab already covers
      // this folder URL (folder deep links share document-shaped paths).
      if (
        existingForPath &&
        !isUuid(existingForPath.id) &&
        openTabs.some((t) => t.type === 'explorer')
      ) {
        const explorerAtFolder = openTabs.find((t) => {
          if (t.type !== 'explorer') return false
          const fp = String(t.explorerData?.folderPath || '')
            .replace(/^\/+/, '')
            .replace(/\/+$/, '')
          const norm = normalizeFolderStoragePath(fp, workspacesRoute) ?? fp
          const want =
            normalizeFolderStoragePath(decodedDocId, workspacesRoute) ?? decodedDocId
          return norm === want || fp === decodedDocId.replace(/^\/+|\/+$/g, '')
        })
        if (explorerAtFolder) {
          if (activeTabId !== explorerAtFolder.id) {
            isUpdatingRef.current = true
            lastTabIdRef.current = explorerAtFolder.id
            lastUrlRef.current = pathname
            onUrlChange(explorerAtFolder.id, {
              type: 'explorer',
              folderPath:
                explorerAtFolder.explorerData?.folderPath ??
                normalizeFolderStoragePath(decodedDocId, workspacesRoute) ??
                decodedDocId,
            })
            setTimeout(() => {
              isUpdatingRef.current = false
            }, 500)
            return
          }
          lastUrlRef.current = pathname
          lastTabIdRef.current = explorerAtFolder.id
          return
        }
      }
      const targetTabId = existingForPath?.id ?? decodedDocId
      if (activeTabId !== targetTabId) {
        isUpdatingRef.current = true
        lastTabIdRef.current = targetTabId
        lastUrlRef.current = pathname
        onUrlChange(targetTabId, { type: 'document' })
        setTimeout(() => {
          isUpdatingRef.current = false
        }, 500)
      }
    } else {
      // Unknown route - update tracking to prevent loops
      const currentTab = openTabs.find(tab => tab.id === activeTabId)
      if (currentTab?.type === 'home' && location.pathname === '/') {
        lastUrlRef.current = location.pathname
        lastTabIdRef.current = activeTabId
      }
    }
  }, [
    location.pathname,
    location.search,
    params,
    activeTabId,
    onUrlChange,
    openTabs,
    syncNavigate,
    workspacesRoute,
    activeWorkspaceIds,
    hideOtherWorkspaces,
  ])
}

